import type { EndpointId, HttpMethod } from "./types";

export const GUIDE_URL = "https://jobindsats.dk/api/";
export const SUPPORT_EMAIL = "jobindsats-api@star.dk";

export const ENDPOINTS: Array<{
  id: EndpointId;
  label: string;
  path: string;
  description: string;
  defaultMethod: HttpMethod;
  help: React.ReactNode;
}> = [
  {
    id: "subjects",
    label: "1 – Hent emner",
    path: "/v3/subjects",
    description: "Returnerer de emnegrupper og emner, som tabellerne er organiseret efter.",
    defaultMethod: "GET",
    help: (
      <>
        <p>
          Dette kald henter en oversigt over alle emner og underemner i databanken, fx det
          overordnede emne ”Ydelser” med underemner som ”A-dagpenge” og ”Kontanthjælp”.
          Hvert emne har et <code>subject_id</code>, som du kan bruge til at afgrænse UC2
          (Hent tabeller). Kaldet kræver ingen parametre ud over <code>format</code>.
        </p>
        <GuideReference call="Kald 1 – Hent emner i databanken" />
      </>
    ),
  },
  {
    id: "tables",
    label: "2 – Hent tabeller",
    path: "/v3/tables",
    description: "Returnerer alle tabeller eller tabellerne under ét emne.",
    defaultMethod: "GET",
    help: (
      <>
        <p>
          Dette kald giver dig overblik over alle målinger (tabeller) i databanken – eller
          kun dem under ét emne, hvis du vælger et emnefilter (parameteren{" "}
          <code>subject_id</code> fra UC1). For hver måling får du bl.a. tabellens id (fx{" "}
          <code>y01a02</code>), opdateringsfrekvens og seneste/næste opdateringsdato. Brug
          tabel-id'et som input til UC3.
        </p>
        <GuideReference call="Kald 2 – Hent målinger under de forskellige emner" />
      </>
    ),
  },
  {
    id: "table",
    label: "3 – Hent tabelmetadata",
    path: "/v3/table/{table_id}",
    description: "Returnerer målingsvariable, perioder, dimensioner og hierarkier for én tabel.",
    defaultMethod: "GET",
    help: (
      <>
        <p>
          Dette kald henter de fulde specifikationer for én måling: målingsvariable
          (mgroups/measures), dimensioner, hierarkier og niveauer (levels), periodetyper
          samt om en dimension er obligatorisk (<code>is_required</code>) eller skjult
          (<code>is_selectable</code>). Disse oplysninger skal du bruge for at kunne bygge
          et gyldigt datakald i UC4.
        </p>
        <p>
          Felterne Emne og Tabel bruges kun til at slå det rigtige tabel-id op – selve
          kaldet sendes med tabel-id'et alene (<code>/v3/table/y01a02</code>).
        </p>
        <GuideReference
          call="Kald 3 – Hent specifikationer for en specifik måling"
          detail="inkl. afsnittene om hierarkier, levels og obligatoriske/skjulte dimensioner"
        />
      </>
    ),
  },
  {
    id: "data",
    label: "4 – Hent data",
    path: "/v3/data/{table_id}",
    description: "Bygger og udfører en valideret dataforespørgsel for én tabel.",
    defaultMethod: "GET",
    help: (
      <>
        <p>Dette kald henter de faktiske tal fra databanken. Du skal altid angive:</p>
        <ul>
          <li>
            <strong>mgroup</strong> – hvilke målingsvariable du vil have (find id'erne i
            UC3 under mgroups/measures)
          </li>
          <li>
            <strong>period</strong> – hvilke perioder, enten specifikke (fx{" "}
            <code>period.M=2024M01,2024M06</code>) eller de seneste N (
            <code>period.M=latest:1</code>). Konsollen sætter altid 1; i dit eget
            kald kan du skrive fx <code>latest:12</code>
          </li>
          <li>
            <strong>hierarchy</strong> – hvilke udfald af dimensioner (fx kommune, køn,
            alder), inkl. eventuelle obligatoriske dimensioner
          </li>
        </ul>
        <table className="help-table">
          <thead>
            <tr>
              <th>Nøgle</th>
              <th>Betydning</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>mgroup</code></td>
              <td>
                Nøglen er mgroup_id, værdien er en liste af measure_id'er – eller{" "}
                <code>*</code> for alle
              </td>
            </tr>
            <tr>
              <td><code>period</code></td>
              <td>
                Nøglen er periodetype (fx <code>M</code>), værdien er perioder eller{" "}
                <code>latest:N</code>
              </td>
            </tr>
            <tr>
              <td><code>hierarchy</code></td>
              <td>
                Nøglen er hierarchy_id (fx <code>_nykom</code>), værdien er value_id'er –
                eller <code>*</code> for alle udfald
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          Mangler der noget, eller er der fejl i parametrene, returnerer API'et en
          fejlbesked, der beskriver hvad der skal rettes – den vises under trin 4.
        </p>
        <GuideReference
          call="Kald 4 – Hent data fra databanken"
          detail="inkl. afsnittene om målingsvariable, dimensioner/hierarkier, levels, obligatoriske dimensioner og perioder"
        />
      </>
    ),
  },
];

function GuideReference({ call, detail }: { call: string; detail?: string }) {
  return (
    <p className="help-reference">
      Svarer til{" "}
      <a href={GUIDE_URL} target="_blank" rel="noreferrer">
        {call}
      </a>{" "}
      i brugervejledningen{detail ? `, ${detail}` : ""}.
    </p>
  );
}
